import {
  RedeemedOfferType,
  Market,
  RedeemedOffer,
} from 'graphql/generated/graphql';

export interface IOffer {
  slug: string;
  translationKey: string;
  restrictions?: {
    subscriptionLength?: 'monthly' | 'yearly';
    subscriptionStatus?: 'active' | 'trial';
  };
  offerType: RedeemedOfferType | 'none';
  showFeedbackInput: boolean;
}

const yearlySubscriptionTooLong: IOffer = {
  slug: 'yearly-too-long',
  translationKey: 'YearlySubscriptionTooLong',
  offerType: RedeemedOfferType.Discount,
  restrictions: {
    subscriptionLength: 'yearly',
  },
  showFeedbackInput: false,
};

const trialPeriodTooShort: IOffer = {
  slug: 'trial-too-short',
  translationKey: 'TrialTooShort',
  offerType: RedeemedOfferType.TrialExtension,
  restrictions: {
    subscriptionStatus: 'trial',
  },
  showFeedbackInput: false,
};

const notUsingEnoughActive: IOffer = {
  slug: 'not-using-enough-active',
  translationKey: 'NotUsingEnough.Active',
  offerType: RedeemedOfferType.Discount,
  restrictions: {
    subscriptionStatus: 'active',
  },
  showFeedbackInput: false,
};

const notUsingEnoughTrial: IOffer = {
  slug: 'not-using-enough-trial',
  translationKey: 'NotUsingEnough.Trial',
  offerType: RedeemedOfferType.TrialExtension,
  restrictions: {
    subscriptionStatus: 'trial',
  },
  showFeedbackInput: false,
};

const audiosNotForMeActive: IOffer = {
  slug: 'audios-not-for-me-active',
  translationKey: 'AudiosNotForMe.Active',
  offerType: RedeemedOfferType.Discount,
  restrictions: {
    subscriptionStatus: 'active',
  },
  showFeedbackInput: false,
};

const audiosNotForMeTrial: IOffer = {
  slug: 'audios-not-for-me-trial',
  translationKey: 'AudiosNotForMe.Trial',
  offerType: RedeemedOfferType.TrialExtension,
  restrictions: {
    subscriptionStatus: 'trial',
  },
  showFeedbackInput: false,
};

const iHaveProblemsWithPlatform: IOffer = {
  slug: 'problems-with-platform',
  translationKey: 'ProblemsWithPlatform',
  offerType: RedeemedOfferType.Discount,
  restrictions: {
    subscriptionStatus: 'active',
  },
  showFeedbackInput: true,
};

const dontNeedFemtasy: IOffer = {
  slug: 'dont-need-femtasy',
  translationKey: 'DontNeedFemtasy',
  offerType: 'none',
  restrictions: {
    subscriptionStatus: 'active',
  },
  showFeedbackInput: false,
};

const other: IOffer = {
  slug: 'other',
  translationKey: 'Other',
  offerType: RedeemedOfferType.Discount,
  restrictions: {
    subscriptionStatus: 'active',
  },
  showFeedbackInput: true,
};

const tooExpensive: IOffer = {
  slug: 'too-expensive',
  translationKey: 'TooExpensive',
  offerType: RedeemedOfferType.Discount,
  showFeedbackInput: false,
};

const deEnOffers = [
  yearlySubscriptionTooLong,
  trialPeriodTooShort,
  notUsingEnoughActive,
  notUsingEnoughTrial,
  audiosNotForMeActive,
  audiosNotForMeTrial,
  iHaveProblemsWithPlatform,
  tooExpensive,
];

const frOffers = [
  iHaveProblemsWithPlatform,
  notUsingEnoughActive,
  notUsingEnoughTrial,
  dontNeedFemtasy,
  yearlySubscriptionTooLong,
  audiosNotForMeActive,
  audiosNotForMeTrial,
  tooExpensive,
  other,
];

const offersConfig = {
  [Market.De]: deEnOffers,
  [Market.EnGb]: deEnOffers,
  [Market.EnUs]: deEnOffers,
  [Market.FrFr]: frOffers,
};

interface IOffers {
  yearly: boolean;
  inTrial: boolean;
  market: Market;
}

export const offerList = ({ yearly, inTrial, market }: IOffers): IOffer[] =>
  offersConfig[market]
    .filter(
      (offer) =>
        !offer.restrictions?.subscriptionLength ||
        offer.restrictions?.subscriptionLength ===
          (yearly ? 'yearly' : 'monthly'),
    )
    .filter(
      (offer) =>
        !offer.restrictions?.subscriptionStatus ||
        offer.restrictions.subscriptionStatus ===
          (inTrial ? 'trial' : 'active'),
    );

export const canOfferBeRedeemed = (
  offerType: RedeemedOfferType | 'none',
  redeemedOffers?: Pick<RedeemedOffer, 'offerType'>[],
) => {
  if (!redeemedOffers) return false;
  if (offerType === 'none') return true;

  return !redeemedOffers.map((offer) => offer.offerType).includes(offerType);
};

export const getOfferBySlug = (slug: string): IOffer | undefined =>
  [...deEnOffers, ...frOffers].find((offer) => offer.slug === slug);
